import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, BedDouble, PlusCircle, FilterIcon, Edit, Trash2, Loader2, TrendingUp, Scale } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import DataTable from '@/components/DataTable';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import Pagination from '@/components/Pagination';
import { useToast } from '@/components/ui/use-toast';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  fetchClientOccupancy,
  createClientOccupancy,
  updateClientOccupancy,
  deleteClientOccupancy,
  exportClientOccupancy,
} from './services/clientOccupancy.services';
import { getClientByLaundryId } from '@/pages/parametrizacion/clients/Services/clients.services.jsx';
import { fetchLaundries } from '@/pages/parametrizacion/laundries/Services/laundries.services';
import { getReceptionDaily } from '@/pages/reception/services/reception.services';

const ITEMS_PER_PAGE = 10;

const getTodayDate = () => new Date().toISOString().split('T')[0];

const getDefaultFilters = (laundryId) => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29, 0, 0, 0).toISOString();
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString();
  return {
    'lavanderia.id': laundryId,
    fecha: { $gte: start, $lte: end },
  };
};

const emptyForm = { clienteId: '', habitacionesOcupacion: '', fecha: getTodayDate() };

// Parsea un string "YYYY-MM-DD" como fecha LOCAL (no UTC).
// `new Date("2026-06-19")` se interpreta como UTC midnight, lo cual en Bogota
// (UTC-5) cae el 18/jun a las 7pm local. Por eso usamos el constructor local
// con año/mes/día para evitar ese desfase.
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const columns = [
  {
    id: 'fecha',
    label: 'Fecha',
    sortable: true,
    render: (row) => {
      const d = new Date(row.fecha);
      return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
  },
  {
    id: 'cliente',
    label: 'Cliente',
    sortable: true,
    render: (row) => row.cliente?.nombre ?? 'N/A',
  },
  {
    id: 'habitacionesOcupacion',
    label: 'Hab. Ocupadas',
    sortable: true,
    render: (row) => row.habitacionesOcupacion ?? 0,
  },
  {
    id: 'habitacionesTotales',
    label: 'Hab. Totales',
    sortable: true,
    render: (row) => row.habitacionesTotales ?? 0,
  },
  {
    id: 'porcentajeOcupacion',
    label: '% Ocupación',
    sortable: true,
    render: (row) => {
      const pct = row.porcentajeOcupacion ?? 0;
      const color = pct >= 80 ? 'text-green-600' : pct >= 50 ? 'text-yellow-600' : 'text-red-500';
      return <span className={`font-semibold ${color}`}>{pct.toFixed(1)}%</span>;
    },
  },
  {
    id: 'actions',
    label: 'Acciones',
    key: 'actions',
    actions: [
      { key: 'edit', label: 'Editar', icon: 'Edit', tooltip: 'Editar', className: 'text-green-600 hover:text-green-700' },
      { key: 'delete', label: 'Eliminar', icon: 'Trash', tooltip: 'Eliminar', className: 'text-red-600 hover:text-red-700' },
    ],
  },
];

const columnsExcel = [
  { key: 'fecha', header: 'Fecha' },
  { key: 'cliente.nombre', header: 'Cliente' },
  { key: 'habitacionesOcupacion', header: 'Hab. Ocupadas' },
  { key: 'habitacionesTotales', header: 'Hab. Totales' },
  { key: 'porcentajeOcupacion', header: '% Ocupación' },
];

const OcupacionClientesPage = () => {
  const { idLavanderia } = useParams();
  const { toast } = useToast();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(getDefaultFilters(idLavanderia));
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [laundryName, setLaundryName] = useState('');
  const [clients, setClients] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Kilos recepcionados por día (consulta colección receptions)
  const [dailyKilos, setDailyKilos] = useState([]);
  const [loadingKilos, setLoadingKilos] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: rows, totalRecords } = await fetchClientOccupancy({
        limit: ITEMS_PER_PAGE,
        page: currentPage,
        query: filters,
      });
      setData(rows || []);
      setTotalItems(totalRecords || 0);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar los registros de ocupación.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Carga los kilos recepcionados por día usando el mismo rango de fechas del filtro.
  const fetchDailyKilos = useCallback(async () => {
    setLoadingKilos(true);
    try {
      // El filtro de ocupación usa `fecha`, pero el endpoint de recepción usa `createdAt`.
      const dateRange = filters?.fecha || filters?.createdAt;
      const query = { 'lavanderia.id': idLavanderia };
      if (dateRange) {
        query.createdAt = {
          $gte: dateRange.$gte,
          $lte: dateRange.$lte,
        };
      }
      const response = await getReceptionDaily({ query, groupBy: 'day' });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setDailyKilos(rows);
    } catch (error) {
      console.error('Error cargando kilos por día:', error);
      setDailyKilos([]);
    } finally {
      setLoadingKilos(false);
    }
  }, [idLavanderia, filters]);

  useEffect(() => {
    fetchDailyKilos();
  }, [fetchDailyKilos]);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const res = await getClientByLaundryId({ query: { 'lavanderia.id': idLavanderia, estado: 'Activo' }, limit: 1000 });
        setClients(Array.isArray(res) ? res : res?.data || []);
      } catch {
        setClients([]);
      }
    };
    loadClients();
  }, [idLavanderia]);

  useEffect(() => {
    const loadName = async () => {
      try {
        const res = await fetchLaundries({ query: { _id: idLavanderia } });
        if (res?.data?.[0]?.nombre) setLaundryName(res.data[0].nombre);
      } catch {
        // silencioso
      }
    };
    loadName();
  }, [idLavanderia]);

  const openCreate = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    const fecha = item.fecha ? new Date(item.fecha).toISOString().split('T')[0] : getTodayDate();
    setForm({
      clienteId: item.cliente?.id ?? '',
      habitacionesOcupacion: String(item.habitacionesOcupacion ?? ''),
      fecha,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const cliente = clients.find((c) => c._id === form.clienteId);
    if (!cliente) {
      toast({ title: 'Error', description: 'Selecciona un cliente.', variant: 'destructive' });
      return;
    }
    const habitacionesOcupacion = parseInt(form.habitacionesOcupacion);
    if (isNaN(habitacionesOcupacion) || habitacionesOcupacion < 0) {
      toast({ title: 'Error', description: 'Ingresa una cantidad válida de habitaciones ocupadas.', variant: 'destructive' });
      return;
    }
    const habitacionesTotales = cliente.habitacionesTotales ?? 0;
    const porcentajeOcupacion = habitacionesTotales > 0
      ? parseFloat(((habitacionesOcupacion / habitacionesTotales) * 100).toFixed(2))
      : 0;

    const [year, month, day] = form.fecha.split('-');
    const fecha = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0)).toISOString();

    const payload = {
      cliente: { id: cliente._id, nombre: cliente.nombre },
      lavanderia: { id: idLavanderia, nombre: laundryName },
      habitacionesOcupacion,
      habitacionesTotales,
      porcentajeOcupacion,
      fecha,
    };

    setIsSaving(true);
    try {
      if (editingItem) {
        await updateClientOccupancy(editingItem._id, payload);
        toast({ title: 'Éxito', description: 'Registro actualizado correctamente.' });
      } else {
        await createClientOccupancy(payload);
        toast({ title: 'Éxito', description: 'Registro creado correctamente.' });
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudo guardar el registro.', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAction = (action, item) => {
    if (action === 'edit') openEdit(item);
    if (action === 'delete') {
      setItemToDelete(item);
      setDeleteDialogOpen(true);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteClientOccupancy(itemToDelete._id);
      toast({ title: 'Éxito', description: 'Registro eliminado.' });
      fetchData();
    } catch {
      toast({ title: 'Error', description: 'No se pudo eliminar el registro.', variant: 'destructive' });
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleFilterChange = (newFilters) => {
    setCurrentPage(1);
    setFilters({ ...newFilters, 'lavanderia.id': idLavanderia });
  };

  const handleRemoveFilter = (key) => {
    if (key === 'lavanderia.id') return;
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const filterFields = [
    {
      id: 'cliente.id',
      label: 'Cliente',
      type: 'select',
      options: clients.map((c) => ({ value: c._id, label: c.nombre })),
    },
    {
      id: 'fecha',
      label: 'Rango de fecha',
      filterType: 'daterange',
    },
  ];

  const avgOcupacion = data.length > 0
    ? (data.reduce((acc, r) => acc + (r.porcentajeOcupacion ?? 0), 0) / data.length).toFixed(1)
    : null;

  // Agrupar ocupación por día y hacer join con kilos recepcionados por día.
  // El resultado se usa tanto para la tabla comparativa como para el gráfico.
  const comparativeByDay = (() => {
    // Mapa de ocupación: yyyy-mm-dd → { ocupacionPromedio, habOcupadas, registros }
    const occMap = new Map();
    data.forEach((r) => {
      if (!r.fecha) return;
      const key = new Date(r.fecha).toISOString().slice(0, 10);
      if (!occMap.has(key)) {
        occMap.set(key, { sumPct: 0, sumHab: 0, count: 0 });
      }
      const bucket = occMap.get(key);
      bucket.sumPct += Number(r.porcentajeOcupacion || 0);
      bucket.sumHab += Number(r.habitacionesOcupacion || 0);
      bucket.count += 1;
    });

    // Unión de fechas: ocupacion + kilos
    const allDates = new Set([
      ...Array.from(occMap.keys()),
      ...dailyKilos.map((k) => (k.date ? new Date(k.date).toISOString().slice(0, 10) : null)).filter(Boolean),
    ]);

    return Array.from(allDates)
      .sort((a, b) => a.localeCompare(b))
      .map((dateKey) => {
        const occ = occMap.get(dateKey);
        const kilos = dailyKilos.find((k) => k.date && new Date(k.date).toISOString().slice(0, 10) === dateKey);
        const fecha = parseLocalDate(dateKey);
        const fechaCorta = `${fecha.getDate().toString().padStart(2, '0')}/${(fecha.getMonth() + 1).toString().padStart(2, '0')}`;
        return {
          dateKey,
          fechaLabel: fechaCorta,
          kilosRecibidos: Number(kilos?.pesoKg || 0),
          kilosLimpios: Number(kilos?.kilosLimpios || 0),
          rechazo: Number(kilos?.rechazoKg || 0),
          recepciones: Number(kilos?.count || 0),
          ocupacionPromedio: occ ? Number((occ.sumPct / occ.count).toFixed(1)) : 0,
          habOcupadas: occ ? occ.sumHab : 0,
          registrosOcupacion: occ ? occ.count : 0,
        };
      })
      // Calculamos kilosPorHabitacion por fila (kilos / habitaciones ocupadas del día).
      // Si no hay habitaciones ocupadas en ese día, devolvemos 0 para no romper la métrica.
      .map((row) => ({
        ...row,
        kilosPorHabitacion:
          row.habOcupadas > 0
            ? Number((row.kilosRecibidos / row.habOcupadas).toFixed(2))
            : 0,
      }));
  })();

  const selectedClient = clients.find((c) => c._id === form.clienteId);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-4 md:p-6 lg:p-8"
    >
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to={`/centros-lavado/${idLavanderia}`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver al Centro de Lavado
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-teal-500/10 p-3 rounded-lg">
              <BedDouble className="h-8 w-8 text-teal-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">Ocupación de Clientes 2</CardTitle>
              <CardDescription>Centro de Lavado: {laundryName || 'Cargando...'}</CardDescription>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setIsFilterOpen(true)}>
              <FilterIcon className="h-4 w-4 mr-2" /> Filtrar
            </Button>
            <ExportExcelButton
              service={exportClientOccupancy}
              filters={filters}
              columns={columnsExcel}
              fileName="Ocupacion_Clientes"
            />
            <Button onClick={openCreate}>
              <PlusCircle className="h-4 w-4 mr-2" /> Registrar Ocupación
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {/* Resumen */}
          {data.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              <div className="p-2.5 rounded-lg shadow-sm border border-teal-300/50 bg-gradient-to-r from-teal-50/10 to-transparent">
                <h3 className="text-xs font-medium text-muted-foreground">Total registros</h3>
                <p className="text-lg font-bold mt-0.5 text-teal-600">{totalItems.toLocaleString()}</p>
              </div>
              {avgOcupacion !== null && (
                <div className="p-2.5 rounded-lg shadow-sm border border-blue-300/50 bg-gradient-to-r from-blue-50/10 to-transparent flex items-center gap-3">
                  <div>
                    <h3 className="text-xs font-medium text-muted-foreground">Ocupación promedio (página)</h3>
                    <p className="text-lg font-bold mt-0.5 text-blue-600">{avgOcupacion}%</p>
                  </div>
                  <TrendingUp className="h-5 w-5 text-blue-400" />
                </div>
              )}
            </div>
          )}

          <div className="w-full">
            <AppliedFilters
              filters={filters}
              fields={filterFields}
              onRemoveFilter={handleRemoveFilter}
              ignoreKeys={['lavanderia.id']}
            />
          </div>

          <DataTable
            data={data}
            columns={columns}
            isLoading={loading}
            onAction={handleAction}
          />
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </CardContent>
      </Card>

      {/* ── Sección: Comparativa Kilos Recepcionados vs % Ocupación ── */}
      <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700/80 p-6 md:p-8 mb-4">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-teal-500 to-emerald-500" />
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-emerald-600 shadow-md shadow-indigo-200 dark:shadow-indigo-900/40">
            <TrendingUp className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Kilos Recepcionados vs % Ocupación</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Join por día entre recepciones (pesoKg) y registros de ocupación de clientes
            </p>
          </div>
        </div>

        {loadingKilos ? (
          <div className="text-center py-8">
            <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
            <span className="text-sm text-muted-foreground">Cargando datos comparativos...</span>
          </div>
        ) : comparativeByDay.length === 0 ? (
          <div className="text-center py-8">
            <span className="text-sm text-muted-foreground">
              Sin datos en el rango de fechas seleccionado.
            </span>
          </div>
        ) : (
          <>
            {/* Gráfico comparativo (kg izquierda, % y kg/hab a la derecha) */}
            <div style={{ width: '100%', height: 360 }} className="mb-6">
              <ResponsiveContainer>
                <ComposedChart
                  data={comparativeByDay}
                  margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="fechaLabel" tick={{ fontSize: 12 }} />
                  <YAxis
                    yAxisId="left"
                    orientation="left"
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Kilos', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#6366f1' } }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[0, 100]}
                    tick={{ fontSize: 12 }}
                    label={{ value: '% Ocupación', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: '#10b981', dx: -10 } }}
                  />
                  <YAxis
                    yAxisId="rightKPH"
                    orientation="right"
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Kg/Hab', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: '#f59e0b', dx: 20 } }}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === 'Kilos Recibidos') return [`${Number(value).toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`, name];
                      if (name === '% Ocupación') return [`${Number(value).toFixed(1)}%`, name];
                      if (name === 'Kilos por Habitación') return [`${Number(value).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg/hab`, name];
                      if (name === 'Habitaciones Ocupadas') return [Number(value).toLocaleString('es-CO'), name];
                      return [value, name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="kilosRecibidos" name="Kilos Recibidos" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="ocupacionPromedio" name="% Ocupación" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="rightKPH" type="monotone" dataKey="kilosPorHabitacion" name="Kilos por Habitación" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Tabla comparativa */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-2 font-semibold text-gray-700 dark:text-gray-300">Fecha</th>
                    <th className="text-right py-2 px-2 font-semibold text-indigo-700 dark:text-indigo-400">Kilos Recibidos</th>
                    <th className="text-right py-2 px-2 font-semibold text-blue-700 dark:text-blue-400">Kilos Limpios</th>
                    <th className="text-right py-2 px-2 font-semibold text-rose-700 dark:text-rose-400">Rechazo</th>
                    <th className="text-right py-2 px-2 font-semibold text-emerald-700 dark:text-emerald-400">% Ocupación</th>
                    <th className="text-right py-2 px-2 font-semibold text-teal-700 dark:text-teal-400">Hab. Ocupadas</th>
                    <th className="text-right py-2 px-2 font-semibold text-amber-700 dark:text-amber-400">Kg / Hab.</th>
                    <th className="text-right py-2 px-2 font-semibold text-gray-700 dark:text-gray-400">Recepciones</th>
                  </tr>
                </thead>
                <tbody>
                  {comparativeByDay.map((row) => (
                    <tr key={row.dateKey} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="py-2 px-2 text-gray-700 dark:text-gray-300">
                        {parseLocalDate(row.dateKey)?.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }) || row.dateKey}
                      </td>
                      <td className="py-2 px-2 text-right font-mono font-semibold text-indigo-700 dark:text-indigo-400">
                        {row.kilosRecibidos.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-blue-700 dark:text-blue-400">
                        {row.kilosLimpios.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-rose-700 dark:text-rose-400">
                        {row.rechazo.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg
                      </td>
                      <td className="py-2 px-2 text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400">
                        {row.ocupacionPromedio > 0 ? `${row.ocupacionPromedio.toFixed(1)}%` : '—'}
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-teal-700 dark:text-teal-400">
                        {row.habOcupadas > 0 ? row.habOcupadas.toLocaleString('es-CO') : '—'}
                      </td>
                      <td className="py-2 px-2 text-right font-mono font-semibold text-amber-700 dark:text-amber-400">
                        {row.kilosPorHabitacion > 0
                          ? `${row.kilosPorHabitacion.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg/hab`
                          : '—'}
                      </td>
                      <td className="py-2 px-2 text-right text-gray-600 dark:text-gray-400">{row.recepciones}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* FilterDrawer */}
      <AnimatePresence>
        {isFilterOpen && (
          <FilterDrawer
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            fields={filterFields}
            initialFilters={filters}
            onApply={handleFilterChange}
          />
        )}
      </AnimatePresence>

      {/* Modal crear/editar */}
      <Dialog open={modalOpen} onOpenChange={(open) => { setModalOpen(open); if (!open) setForm(emptyForm); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BedDouble className="h-5 w-5 text-teal-600" />
              {editingItem ? 'Editar Ocupación' : 'Registrar Ocupación'}
            </DialogTitle>
            <DialogDescription>
              Registra la ocupación diaria de habitaciones de un cliente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div>
              <Label className="text-sm">Fecha</Label>
              <Input
                type="date"
                value={form.fecha}
                max={getTodayDate()}
                onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-sm">Cliente</Label>
              <Select
                value={form.clienteId}
                onValueChange={(v) => setForm((p) => ({ ...p, clienteId: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar cliente..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c._id} value={c._id}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm">Habitaciones ocupadas</Label>
              <Input
                type="number"
                min="0"
                value={form.habitacionesOcupacion}
                onChange={(e) => setForm((p) => ({ ...p, habitacionesOcupacion: e.target.value }))}
                placeholder="0"
                className="mt-1"
              />
            </div>

            {selectedClient && (
              <div className="rounded-md bg-muted/40 px-3 py-2 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Hab. totales del cliente:</span>
                  <span className="font-medium">{selectedClient.habitacionesTotales ?? 'No configurado'}</span>
                </div>
                {selectedClient.habitacionesTotales > 0 && form.habitacionesOcupacion !== '' && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">% Ocupación:</span>
                    <span className="font-semibold text-teal-600">
                      {((parseInt(form.habitacionesOcupacion) / selectedClient.habitacionesTotales) * 100).toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setModalOpen(false); setForm(emptyForm); }}
            >
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-teal-600 hover:bg-teal-700 text-white">
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editingItem ? 'Actualizar' : 'Registrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminar */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el registro de ocupación del cliente
              <strong> {itemToDelete?.cliente?.nombre}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default OcupacionClientesPage;
